import { PerspectiveCamera } from '@react-three/drei';
import { Hearts, preloader as HeartsPreloader } from './icons/heart';
import { Dashboard, preloader as DashboardPreloader } from './icons/dashboard';
import { Headphones, preloader as HeadphonesPreloader } from './icons/headphones';
import { Promotions, preloader as PromotionsPreloader } from './icons/promotions';
import { Graph, preloader as GraphPreloader } from './icons/graph';
import { useEffect, useMemo, useRef } from 'react';
import { HeroThreeAnimationBridge, hookOnProgress } from './bridge';
import { reaction } from 'mobx';
import { HomeHero } from 'scripts/animations/three/home-hero';
import { Rotator } from 'scripts/animations/rotator';
import { Disposer } from '@zajno/common/functions/disposer';
import { gsap } from 'gsap';
import { BreakpointType, Breakpoints } from 'app/services/breakpoints';
import { useThree } from '@react-three/fiber';
import { BackfaceMaterialBack, BackfaceMaterialFront, PrimaryIconMaterial, SecondaryIconMaterial } from './icons/common/materials';
import { runEvent } from 'scripts/animations/three/utils/run';
import { PrimaryLight, SecondaryLight } from './icons/common/lights';
import { preloadAsset } from './icons/common/preload';
import { observer } from 'mobx-react-lite';
import { PreCompiler } from 'scripts/animations/preCompiler';
import { isSafari } from 'scripts/utils/detectBrowser';

export type SceneProps = {
    backgroundTarget: string | HTMLElement;
    uiBottomTargetId: string;
};

type Props = JSX.IntrinsicElements['group'] & SceneProps;

// TODO the following baking of constants assumes that on breakpoint change this module will be reloaded
const CurrentBreakpointType = Breakpoints.Current.breakpoint.name as BreakpointType;
const IsCurrentDesktop = CurrentBreakpointType === BreakpointType.Desktop;
const GraphOffset = 200; // estimated distance from top of graph to the virtual top of composition (top icons)
const OffScreenOffset = 1;

// in this aspect ratio the scale should be just 1
const IdealAspectRatio = 16 / 9;

preloadAsset(GraphPreloader);
if (IsCurrentDesktop) {
    preloadAsset(PromotionsPreloader);
    preloadAsset(HeartsPreloader);
    preloadAsset(DashboardPreloader);
    preloadAsset(HeadphonesPreloader);
}

// this will be resolved on scene compile
// HeroThreeAnimationBridge.SceneLoaded.resolve();

export const Scene = observer((propsAll: Props) => {

    const isActive = HeroThreeAnimationBridge.isActive.value;
    const isExternalInitialized = HeroThreeAnimationBridge.isExternalInitialized.value;

    const rootRef = useRef<THREE.Group>();
    const rotateTargetRef = useRef<THREE.Group>();
    const mainCameraRef = useRef<THREE.PerspectiveCamera>();

    const { backgroundTarget, uiBottomTargetId, ...props } = propsAll;
    const backgroundTargetRef = useRef(backgroundTarget);
    backgroundTargetRef.current = backgroundTarget;

    const mainVisible = props.visible ?? (isActive && isExternalInitialized);
    const firstActivation = useRef(true);

    const rendererSize = useThree(state => state.size);
    const aspect = rendererSize.width / rendererSize.height;
    // for vertical screens we should scale down just by aspect ratio
    // for horizontal we should scale so it will be as Ideal aspect ratio
    const mainScale = aspect < 1 ? aspect : (1 * aspect / IdealAspectRatio);

    const cameraOffset = useMemo(() => {
        const uiRectOffset = document.getElementById(uiBottomTargetId)?.getBoundingClientRect()?.bottom || 0;
        return {
            // how far camera should be at the start
            offScreen: rendererSize.height * OffScreenOffset,
            // how much we should move from the center of screen to the bottom of the UI
            initial: uiRectOffset - rendererSize.height / 2 + GraphOffset * mainScale,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should be calculated only once on the first render
    }, []);

    useEffect(() => {
        if (!mainVisible) {
            return;
        }

        const disposer = new Disposer();

        const idle = runEvent(
            IsCurrentDesktop
                ? HomeHero.Events.Idle
                : HomeHero.Events.IdleResponsive,
            {
                root: rootRef.current,
                immediateRender: true,
            },
        );
        const scroll = runEvent(
            IsCurrentDesktop
                ? HomeHero.Events.Scroll
                : HomeHero.Events.ScrollResponsive,
            {
                root: rootRef.current,
                computed: {
                    [HomeHero.States.Camera.Keys.Initial]: cameraOffset.initial,
                },
                immediateRender: true,
            },
        );

        let start: gsap.core.Timeline = null;
        if (firstActivation.current) {
            start = runEvent(
                IsCurrentDesktop
                    ? HomeHero.Events.Start
                    : HomeHero.Events.StartResponsive,
                {
                    root: rootRef.current,
                    computed: {
                        [HomeHero.States.Camera.Keys.Offscreen]: cameraOffset.offScreen,
                        [HomeHero.States.Camera.Keys.Initial]: cameraOffset.initial,
                    },
                    rootTimelineVars: { delay: isSafari ? 1.2 : 0 },
                },
            ).add(() => HeroThreeAnimationBridge.SceneAnimated.resolve());
        }

        [idle, start, scroll].filter(Boolean).forEach(tl => disposer.add(() => tl.kill()));

        firstActivation.current = false;

        scroll.paused(true);

        const doHook = () => {
            disposer.add(
                hookOnProgress(scroll, HeroThreeAnimationBridge.progress),
            );

            HeroThreeAnimationBridge.isStarted.setTrue();
        };

        // enable scroll hook only after start animation
        if (start) {
            start.add(doHook);
        } else {
            // hack to make sure progress will not stuck at middle if we're going to set it to 0
            scroll.progress(1);
            if (HeroThreeAnimationBridge.progress.value < 0.05) {
                gsap.fromTo(scroll, { progress: 0.03 }, { progress: 0, duration: 0.1, delay: 0.1 });
            }

            doHook();
        }

        disposer.add(HeroThreeAnimationBridge.isStarted.setFalse);

        return () => disposer.dispose();
    }, [mainVisible, cameraOffset]);

    // video playing => changing colors
    useEffect(() => {
        if (!mainVisible) {
            return;
        }

        const backfaceMaterials = [
            BackfaceMaterialFront,
            BackfaceMaterialBack,
        ];

        const backface = runEvent(HomeHero.Events.createBackfaceColorIdleEvent(backfaceMaterials));
        const background = runEvent(HomeHero.Events.createBackgroundColorIdleEvent(backgroundTargetRef.current));

        const tlIdle = gsap.timeline()
            .add(backface, 0)
            .add(background, 0)
        ;

        const setupColorsIdle = () => {
            const primary = runEvent(
                HomeHero.Events.createPrimaryColorIdleEvent(PrimaryIconMaterial),
                { root: rootRef.current },
            );
            tlIdle.add(primary, 0);

            const secondary = runEvent(
                HomeHero.Events.createSecondaryColorIdleEvent(SecondaryIconMaterial),
                { root: rootRef.current },
            );
            tlIdle.add(secondary, 0);
        };

        setupColorsIdle();

        tlIdle.repeat(-1);
        tlIdle.paused(true);

        let tlReset: gsap.core.Timeline;

        const runResetBackface = () => {
            tlReset = runEvent(HomeHero.Events.createBackfaceResetColorEvent(backfaceMaterials));

            if (HeroThreeAnimationBridge.progress.value >= 0.99) { // effectively is '1'
                tlReset.progress(1);
            }
        };

        const restart = () => {
            tlIdle.progress(0);
            tlIdle.paused(false);
        };

        return reaction(
            () => HeroThreeAnimationBridge.isVideoPlaying.value,
            isPlaying => {
                if (isPlaying) {
                    if (tlReset) {
                        const tlr = tlReset;
                        tlReset = null;

                        tlr.reverse();
                        tlr.eventCallback('onReverseComplete', () => {
                            restart();
                            tlr.kill();
                        });
                    } else {
                        restart();
                    }

                } else {
                    tlIdle.paused(true);

                    // run reset animation only if it's the end of scroll
                    if (!HeroThreeAnimationBridge.isProgressThresholdNotReached.value || HeroThreeAnimationBridge.isVideoPlaceholder.value) {
                        runResetBackface();
                    }
                }
            },
            { fireImmediately: true },
        );
    }, [mainVisible]);

    return (
        <group
            ref={rootRef}
            {...props}
            dispose={null}
            visible={mainVisible}
        >
            <PreCompiler
                name='HeroThreeAnimation'
                manualPromise={HeroThreeAnimationBridge.SceneLoaded}
                rootRef={rootRef}
            />
            {mainVisible && <Rotator target={rotateTargetRef} scale={0.1} />}

            <primitive object={PrimaryLight} />
            <primitive object={SecondaryLight} />
            <group
                ref={rotateTargetRef}
                scale={mainScale}
            >
                <Graph />
                {IsCurrentDesktop && <Hearts />}
                {IsCurrentDesktop && <Dashboard />}
                {IsCurrentDesktop && <Headphones />}
                {IsCurrentDesktop && <Promotions />}
            </group>
            <PerspectiveCamera
                ref={mainCameraRef}
                name="Camera"
                makeDefault
                far={100000}
                near={70}
                fov={45}
                position={[0, cameraOffset.offScreen, 1000]}
            />
        </group>
    );
});
